/**
 * Money on the priced billing screens.
 *
 * No i18n and no UI kit. The caller passes its number formatter, which
 * formats in the merchant's language and region.
 */

/**
 * A billing amount in USD: "$29", "$1,499.50".
 *
 * Every billing charge is in USD (the backend pushes `currencyCode: 'USD'`),
 * so the symbol is part of the rule rather than the shop's currency.
 *
 * THE CENTS RULE is the backend's (`billing/services/notifications.py`
 * `_price_line`): a whole number of dollars drops them ("$29", not
 * "$29.00" — the wire carries `str(Decimal)`), anything else keeps them.
 * Every listed tier price is a whole dollar; a negotiated Enterprise price
 * can carry cents, and rounding it would disagree with the charge Shopify
 * approves.
 *
 * THROWS on a missing or non-numeric amount instead of printing "$0" next
 * to a live Subscribe button. A missing price is a broken payload.
 *
 * @param {number|string} amount
 * @param {(value: number, decimals: number) => string} formatNumber
 * @returns {string}
 */
export function usd(amount, formatNumber) {
  const value = Number(amount);
  if (amount == null || amount === '' || Number.isNaN(value)) {
    throw new Error(`usd: missing or non-numeric price (${amount})`);
  }
  // Quantize to cents BEFORE asking whether there are any: a value that is
  // a whole number of dollars only after rounding prints as one.
  const cents = Math.round(value * 100);
  return `$${formatNumber(cents / 100, cents % 100 === 0 ? 0 : 2)}`;
}

/**
 * The three figures a priced card shows for the selected interval.
 *
 *   perMonth      — the monthly rate, or the annual price's exact per-month
 *                   equivalent ($250/yr → $20.83; `usd` prints cents only
 *                   when there are any).
 *   strikethrough — the monthly rate beside the annual per-month figure,
 *                   when it is higher: that IS the saving. Null otherwise.
 *   billed        — the amount actually charged per period, for the CTA.
 *
 * `annual` is a whole year's charge, never a rate: a per-month figure a
 * merchant multiplies back must land on the number they are charged.
 */
export function intervalPrices({ monthly, annual, isAnnual }) {
  const perMonth = isAnnual ? annual / 12 : monthly;
  return {
    perMonth,
    strikethrough: monthly > perMonth ? monthly : null,
    billed: isAnnual ? annual : monthly,
  };
}
